import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { X, Pencil, KeyRound, UserX, UserCheck, RefreshCw, Eye, EyeOff, LogOut } from 'lucide-react';
import { usuariosAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from './ui';
import { invalidateUsuarios } from '../services/cachedCatalogs';
import { selectStyles } from '../utils/adminShared';
import { generarPasswordSeguro } from '../utils/helpers';
import { useModalKeyboard } from '../hooks/useModalKeyboard';
import { Tooltip, Modal, Button } from './ui';
import SlackMemberIdHelp from './SlackMemberIdHelp';
import { Plus } from 'lucide-react';

const rolOptions = [
    { value: 'superadmin', label: 'Super Admin' },
    { value: 'admin', label: 'Admin' },
    { value: 'gestor_proyecto', label: 'Gestor de Proyectos' },
    { value: 'comercial', label: 'Comercial' },
    { value: 'personal', label: 'Personal' },
];

const rolLabel = (rol) => rolOptions.find(r => r.value === rol)?.label || rol;

const UsuariosPanel = () => {
    const { user: currentUser } = useAuth();
    const confirm = useConfirm();
    const esSuperadmin = currentUser?.rol === 'superadmin';
    const [loading, setLoading] = useState(true);
    const [usuarios, setUsuarios] = useState([]);
    const [nuevoUsuario, setNuevoUsuario] = useState({ nombre: '', email: '', password: '', rol: 'personal', slack_member_id: '' });
    const [showCrearModal, setShowCrearModal] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPass, setShowNewPass] = useState(false);
    const [editUsuario, setEditUsuario] = useState(null);
    const [passUsuario, setPassUsuario] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmResetPass, setConfirmResetPass] = useState('');
    const [showPassModal, setShowPassModal] = useState(false);

    // a11y: Escape cierra cada modal + bloquea scroll del body
    useModalKeyboard({ open: !!editUsuario, onClose: () => setEditUsuario(null) });
    useModalKeyboard({ open: !!passUsuario, onClose: () => { setPassUsuario(null); setNewPassword(''); setConfirmResetPass(''); setShowPassModal(false); } });

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        setLoading(true);
        try {
            const res = await usuariosAPI.getAll();
            setUsuarios(res.data || []);
        } catch (err) {
            console.error('Error al cargar usuarios:', err);
        } finally {
            setLoading(false);
        }
    };

    const agregarUsuario = async () => {
        if (!nuevoUsuario.nombre.trim() || !nuevoUsuario.email.trim() || !nuevoUsuario.password) {
            toast.error('Todos los campos son obligatorios');
            return;
        }
        if (nuevoUsuario.password !== confirmPassword) {
            toast.error('Las contraseñas no coinciden');
            return;
        }
        try {
            await usuariosAPI.create(nuevoUsuario);
            invalidateUsuarios();
            setNuevoUsuario({ nombre: '', email: '', password: '', rol: 'personal', slack_member_id: '' });
            setConfirmPassword('');
            setShowNewPass(false);
            setShowCrearModal(false);
            toast.success('Usuario creado correctamente');
            cargarDatos();
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    const toggleUsuarioActivo = async (id, activo) => {
        try {
            await usuariosAPI.update(id, { activo: !activo });
            invalidateUsuarios();
            cargarDatos();
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    // Solo superadmin. Cierra TODAS las sesiones activas del usuario (todos
    // los dispositivos y navegadores donde este logueado).
    const forzarCierreSesion = async (u) => {
        if (u.id === currentUser?.id) return;
        const ok = await confirm({
            title: '¿Cerrar todas las sesiones?',
            message: <p>Se cerrarán todas las sesiones activas de <strong>{u.nombre}</strong> en cualquier dispositivo. Tendrá que iniciar sesión de nuevo.</p>,
            confirmText: 'Cerrar sesiones',
            danger: true
        });
        if (!ok) return;
        try {
            await usuariosAPI.forceLogout(u.id);
            toast.success(`Sesiones de ${u.nombre} cerradas`);
        } catch (err) {
            toast.error(err.message || 'No pudimos cerrar las sesiones');
        }
    };

    const guardarEditUsuario = async () => {
        if (!editUsuario) return;
        if (!editUsuario.nombre.trim() || !editUsuario.email.trim()) {
            toast.error('Nombre y email son obligatorios');
            return;
        }
        try {
            await usuariosAPI.update(editUsuario.id, {
                nombre: editUsuario.nombre,
                email: editUsuario.email,
                rol: editUsuario.rol,
                slack_member_id: editUsuario.slack_member_id || null
            });
            invalidateUsuarios();
            toast.success('Usuario actualizado');
            setEditUsuario(null);
            cargarDatos();
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    const generarPassword = () => generarPasswordSeguro(12);

    const guardarPassword = async () => {
        if (!passUsuario || !newPassword) {
            toast.error('La contraseña es obligatoria');
            return;
        }
        if (newPassword !== confirmResetPass) {
            toast.error('Las contraseñas no coinciden');
            return;
        }
        try {
            await usuariosAPI.resetPassword(passUsuario.id, newPassword);
            toast.success('Contraseña actualizada');
            setPassUsuario(null);
            setNewPassword('');
            setConfirmResetPass('');
            setShowPassModal(false);
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    if (loading) {
        return <div className="loading">Cargando usuarios...</div>;
    }

    return (
    <>
        <div className="card">
            <div className="card-header">
                <h2>Usuarios</h2>
            </div>
            <div className="admin-content">
                <div className="admin-section-header">
                    <h3 className="h3">Usuarios</h3>
                    <Button
                        variant="primary"
                        size="sm"
                        leftIcon={<Plus size={14} />}
                        onClick={() => setShowCrearModal(true)}
                    >
                        Nuevo usuario
                    </Button>
                </div>
                <div className="tableWrap">
                    <table className="galTable">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Email</th>
                                <th>Rol</th>
                                <th>Estado</th>
                                <th>Ultimo acceso</th>
                                <th style={{ textAlign: 'center' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {usuarios.map(u => (
                                <tr key={u.id} className={!u.activo ? 'inactive' : ''}>
                                    <td>{u.nombre}</td>
                                    <td>{u.email}</td>
                                    <td>
                                        <span className={`badge ${u.rol === 'superadmin' ? 'warning' : u.rol === 'admin' ? 'success' : 'info'}`}>
                                            {rolLabel(u.rol)}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`badge ${u.activo ? 'success' : 'danger'}`}>
                                            {u.activo ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </td>
                                    <td>{u.ultimo_acceso ? new Date(u.ultimo_acceso).toLocaleString() : '-'}</td>
                                    <td>
                                        <div className="acciones-btns" style={{ justifyContent: 'center' }}>
                                            <Tooltip content="Editar usuario">
                                                <button
                                                    className="btn-icon edit"
                                                    onClick={() => setEditUsuario({ id: u.id, nombre: u.nombre, email: u.email, rol: u.rol || 'personal', slack_member_id: u.slack_member_id || '' })}
                                                    aria-label="Editar usuario"
                                                >
                                                    <Pencil size={16} />
                                                </button>
                                            </Tooltip>
                                            <Tooltip content="Cambiar contraseña">
                                                <button
                                                    className="btn-icon password"
                                                    onClick={() => { setPassUsuario(u); setNewPassword(''); }}
                                                    aria-label="Cambiar contraseña"
                                                >
                                                    <KeyRound size={16} />
                                                </button>
                                            </Tooltip>
                                            <Tooltip content={u.activo ? 'Desactivar usuario' : 'Activar usuario'}>
                                                <button
                                                    className={`btn-icon ${u.activo ? 'text-danger' : 'text-success'}`}
                                                    onClick={() => toggleUsuarioActivo(u.id, u.activo)}
                                                    aria-label={u.activo ? 'Desactivar' : 'Activar'}
                                                >
                                                    {u.activo ? <UserX size={16} /> : <UserCheck size={16} />}
                                                </button>
                                            </Tooltip>
                                            {esSuperadmin && (() => {
                                                // Botón siempre visible para superadmin — se deshabilita cuando
                                                // no aplica (usuario inactivo o es el propio) en vez de esconderlo.
                                                const esPropio = u.id === currentUser?.id;
                                                const puedeCerrar = u.activo && !esPropio;
                                                const tooltipTxt = esPropio
                                                    ? 'No puedes cerrar tu propia sesión desde aquí'
                                                    : (!u.activo
                                                        ? 'Sin sesiones activas'
                                                        : 'Cerrar todas sus sesiones');
                                                return (
                                                    <Tooltip content={tooltipTxt}>
                                                        <button
                                                            className={`btn-icon ${puedeCerrar ? 'text-warning' : ''}`}
                                                            onClick={() => puedeCerrar && forzarCierreSesion(u)}
                                                            disabled={!puedeCerrar}
                                                            aria-label={tooltipTxt}
                                                        >
                                                            <LogOut size={16} />
                                                        </button>
                                                    </Tooltip>
                                                );
                                            })()}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        {/* Modal: Editar Usuario */}
        {editUsuario && (
            <div className="modal-overlay" onClick={() => setEditUsuario(null)}>
                <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                    <div className="modal-header">
                        <h3 className="h3">Editar Usuario</h3>
                        <button
                            className="btn-icon"
                            onClick={() => setEditUsuario(null)}
                        >
                            <X size={20} />
                        </button>
                    </div>
                    <div className="modal-body">
                        <label className="field">
                            <span className="label">Nombre *</span>
                            <input
                                className="input"
                                type="text"
                                value={editUsuario.nombre}
                                onChange={(e) => setEditUsuario(prev => ({ ...prev, nombre: e.target.value }))}
                            />
                        </label>
                        <label className="field">
                            <span className="label">Email *</span>
                            <input
                                className="input"
                                type="email"
                                value={editUsuario.email}
                                onChange={(e) => setEditUsuario(prev => ({ ...prev, email: e.target.value }))}
                            />
                        </label>
                        <label className="field">
                            <span className="label">Rol</span>
                            <Select
                                styles={selectStyles}
                                options={rolOptions}
                                value={rolOptions.find(r => r.value === editUsuario.rol)}
                                onChange={(opt) => setEditUsuario(prev => ({ ...prev, rol: opt.value }))}
                                isSearchable={false}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </label>
                        <label className="field">
                            <span className="label">Slack Member ID (opcional)</span>
                            <input
                                className="input"
                                type="text"
                                placeholder="U07ABCDE123"
                                value={editUsuario.slack_member_id || ''}
                                onChange={(e) => setEditUsuario(prev => ({ ...prev, slack_member_id: e.target.value }))}
                            />
                            <SlackMemberIdHelp />
                        </label>

                        <div className="divider" />

                        <div className="form-row" style={{ justifyContent: 'flex-end' }}>
                            <button className="btn ghost" onClick={() => setEditUsuario(null)}>Cancelar</button>
                            <button className="btn neon" onClick={guardarEditUsuario}>Guardar</button>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* Modal: Cambiar Contraseña */}
        {passUsuario && (
            <div className="modal-overlay" onClick={() => { setPassUsuario(null); setNewPassword(''); setConfirmResetPass(''); setShowPassModal(false); }}>
                <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                    <div className="modal-header">
                        <h3 className="h3">Cambiar Contraseña</h3>
                        <button
                            className="btn-icon"
                            onClick={() => { setPassUsuario(null); setNewPassword(''); setConfirmResetPass(''); setShowPassModal(false); }}
                        >
                            <X size={20} />
                        </button>
                    </div>
                    <div className="modal-body">
                        <p className="empty-message">
                            Usuario: <strong>{passUsuario.nombre}</strong>
                        </p>
                        <label className="field">
                            <span className="label">Nueva contraseña *</span>
                            <div className="input-password-wrap">
                                <input
                                    className="input"
                                    type={showPassModal ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Mínimo 8 caracteres"
                                />
                                <Tooltip content={showPassModal ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowPassModal(!showPassModal)}
                                        tabIndex={-1}
                                        aria-label={showPassModal ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                    >
                                        {showPassModal ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </Tooltip>
                            </div>
                        </label>
                        <label className="field">
                            <span className="label">Confirmar contraseña *</span>
                            <div className="input-password-wrap">
                                <input
                                    className="input"
                                    type={showPassModal ? 'text' : 'password'}
                                    value={confirmResetPass}
                                    onChange={(e) => setConfirmResetPass(e.target.value)}
                                    placeholder="Repetir contraseña"
                                />
                            </div>
                        </label>
                        <button
                            type="button"
                            className="btn ghost"
                            onClick={() => {
                                const pass = generarPassword();
                                setNewPassword(pass);
                                setConfirmResetPass(pass);
                                setShowPassModal(true);
                            }}
                            style={{ alignSelf: 'flex-start' }}
                        >
                            <RefreshCw size={16} /> Generar
                        </button>

                        <div className="divider" />

                        <div className="form-row" style={{ justifyContent: 'flex-end' }}>
                            <button className="btn ghost" onClick={() => { setPassUsuario(null); setNewPassword(''); setConfirmResetPass(''); setShowPassModal(false); }}>Cancelar</button>
                            <button className="btn neon" onClick={guardarPassword}>Cambiar</button>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* Modal: Crear nuevo usuario */}
        <Modal
            open={showCrearModal}
            onClose={() => setShowCrearModal(false)}
            title="Nuevo usuario"
            size="lg"
            footer={
                <>
                    <Button variant="ghost" onClick={() => setShowCrearModal(false)}>Cancelar</Button>
                    <Button variant="primary" onClick={agregarUsuario}>Crear usuario</Button>
                </>
            }
        >
            <div className="form-grid-2col">
                <label className="field">
                    <span className="label">Nombre *</span>
                    <input
                        className="input"
                        type="text"
                        value={nuevoUsuario.nombre}
                        onChange={(e) => setNuevoUsuario(n => ({ ...n, nombre: e.target.value }))}
                    />
                </label>
                <label className="field">
                    <span className="label">Email *</span>
                    <input
                        className="input"
                        type="email"
                        value={nuevoUsuario.email}
                        onChange={(e) => setNuevoUsuario(n => ({ ...n, email: e.target.value }))}
                    />
                </label>
                <label className="field">
                    <span className="label">Contraseña *</span>
                    <div className="input-password-wrap">
                        <input
                            className="input"
                            type={showNewPass ? 'text' : 'password'}
                            value={nuevoUsuario.password}
                            onChange={(e) => setNuevoUsuario(n => ({ ...n, password: e.target.value }))}
                            placeholder="Mínimo 8 caracteres"
                        />
                        <Tooltip content={showNewPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowNewPass(!showNewPass)}
                                tabIndex={-1}
                                aria-label={showNewPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                            >
                                {showNewPass ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </Tooltip>
                    </div>
                </label>
                <label className="field">
                    <span className="label">Confirmar contraseña *</span>
                    <div className="input-password-wrap">
                        <input
                            className="input"
                            type={showNewPass ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Repetir contraseña"
                        />
                    </div>
                </label>
                <label className="field">
                    <span className="label">Rol</span>
                    <Select
                        styles={selectStyles}
                        options={rolOptions}
                        value={rolOptions.find(r => r.value === nuevoUsuario.rol)}
                        onChange={(opt) => setNuevoUsuario(n => ({ ...n, rol: opt.value }))}
                        isSearchable={false}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                    />
                </label>
                <label className="field">
                    <span className="label">Slack Member ID</span>
                    <input
                        className="input"
                        type="text"
                        placeholder="U07ABCDE123"
                        value={nuevoUsuario.slack_member_id}
                        onChange={(e) => setNuevoUsuario(n => ({ ...n, slack_member_id: e.target.value }))}
                    />
                    <SlackMemberIdHelp />
                </label>
            </div>
            <div className="mt-3 flex justify-end">
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    leftIcon={<RefreshCw size={14} />}
                    onClick={() => {
                        const pass = generarPassword();
                        setNuevoUsuario(n => ({ ...n, password: pass }));
                        setConfirmPassword(pass);
                        setShowNewPass(true);
                    }}
                >
                    Generar contraseña segura
                </Button>
            </div>
        </Modal>
    </>
    );
};

export default UsuariosPanel;
